import { useMemo, useEffect, useRef, useState } from 'react';
import { STATUTS } from '../utils/constants';
import { countUniqueNums } from '../utils/groupUtils';
import { TIERED, SIMPLE, tierProgress, simpleProgress, TIER_COLORS } from '../utils/badges';
import { loadTotals, useSeriesSettings } from '../utils/seriesTotals';
import { genreColor } from '../utils/genreColors';

const F = "'Inter',-apple-system,sans-serif";
const D = "'Bangers','Arial Black',cursive";
const v = n => `var(${n})`;

const PALETTE = ['var(--orange)','#F5C842','#3B6FBF','#5A9E7A','#9B59B6','#E74C3C','#1ABC9C','#F39C12','#60A5FA','#34D399','#F472B6','#A78BFA'];

/* ── Donut chart (Canvas) ───────────────────────────────── */
function DonutChart({ data, size=160, hole=0.6 }) {
  const ref = useRef();
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !data?.length) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width  = size+'px';
    canvas.style.height = size+'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const cx = size/2, cy = size/2, r = size/2 - 4;
    const total = data.reduce((s,d)=>s+d.val,0);
    if (!total) return;
    let angle = -Math.PI/2;
    data.forEach(d => {
      const slice = (d.val/total) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, angle, angle+slice);
      ctx.closePath();
      ctx.fillStyle = d.color;
      ctx.fill();
      angle += slice;
    });
    // Trou central
    ctx.beginPath();
    ctx.arc(cx, cy, r*hole, 0, Math.PI*2);
    // Lire la couleur CSS au runtime
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--surface').trim() || '#FFFFFF';
    ctx.fill();
  }, [data, size, hole]);

  return <canvas ref={ref} style={{display:'block'}}/>;
}

/* ── Bar chart horizontal ───────────────────────────────── */
function HBar({ label, val, max, color, total }) {
  const pct = max>0 ? (val/max*100) : 0;
  const pctTotal = total>0 ? Math.round(val/total*100) : 0;
  return (
    <div style={{marginBottom:'10px'}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
        <span style={{fontSize:'13px',fontWeight:500,color:v('--ink'),fontFamily:F,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'65%'}}>{label}</span>
        <span style={{fontSize:'12px',fontWeight:700,color:v('--muted'),fontFamily:F,flexShrink:0}}>{val} <span style={{fontWeight:400,fontSize:'11px'}}>({pctTotal}%)</span></span>
      </div>
      <div style={{height:'7px',background:v('--border'),borderRadius:'99px',overflow:'hidden'}}>
        <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:'99px',transition:'width .6s ease'}}/>
      </div>
    </div>
  );
}

/* ── Section card ───────────────────────────────────────── */
const Card = ({title,children,span=1}) => (
  <div className={span>1?'stat-card-span':undefined} style={{background:v('--surface'),border:`1px solid ${v('--border')}`,borderRadius:'16px',padding:'1.25rem',boxShadow:v('--card-shadow'),gridColumn:`span ${span}`,minWidth:0,overflow:'hidden'}}>
    <div style={{fontSize:'11px',fontWeight:700,color:v('--orange'),textTransform:'uppercase',letterSpacing:'.07em',fontFamily:F,marginBottom:'1rem'}}>{title}</div>
    {children}
  </div>
);

/* ── Big number ─────────────────────────────────────────── */
const BigNum = ({val,label,color,sub}) => (
  <div style={{textAlign:'center'}}>
    <div style={{fontFamily:D,fontSize:'2.2rem',color:color||v('--orange'),letterSpacing:'.03em',lineHeight:1}}>{val}</div>
    <div style={{fontSize:'11px',fontWeight:600,color:v('--muted'),textTransform:'uppercase',letterSpacing:'.06em',marginTop:'4px',fontFamily:F}}>{label}</div>
    {sub&&<div style={{fontSize:'12px',color:v('--subtle'),fontFamily:F,marginTop:'2px'}}>{sub}</div>}
  </div>
);

/* ── Grille de badges (à paliers + simples) ───────────────── */
const PRISM_GRADIENT = 'linear-gradient(135deg,#F04E1A,#F5C842,#3B6FBF,#9B59B6,#16A34A)';
const tierColor = name => name ? (TIER_COLORS[name]==='prismatic' ? PRISM_GRADIENT : TIER_COLORS[name]) : null;

const TierRing = ({ tierName, size=48, children }) => (
  <div style={{width:size,height:size,borderRadius:'50%',padding: tierName?'3px':'0',background: tierName ? tierColor(tierName) : 'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
    <div style={{width:'100%',height:'100%',borderRadius:'50%',background:v('--surface'),display:'flex',alignItems:'center',justifyContent:'center'}}>
      {children}
    </div>
  </div>
);

const tileStyle = got => ({
  textAlign:'center', padding:'12px 6px 10px', borderRadius:'12px', cursor:'pointer',
  border:`1.5px solid ${got?v('--orange'):v('--border')}`,
  background: got ? 'rgba(240,78,26,.08)' : v('--surface2'),
  fontFamily:F, display:'flex', flexDirection:'column', alignItems:'center',
});

const BadgeGrid = ({ bds }) => {
  useSeriesSettings(); // re-render quand les totaux arrivent du cloud
  const totals = loadTotals();
  const [selected, setSelected] = useState(null); // { kind:'tiered'|'simple', data }

  return (
    <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(96px,1fr))',gap:'10px'}}>
        {TIERED.map(fam => {
          const prog = tierProgress(fam, bds, totals);
          const got = prog.tierIndex >= 0;
          return (
            <button key={fam.id} onClick={()=>setSelected({kind:'tiered',data:fam})} style={tileStyle(got)}>
              <TierRing tierName={prog.current?.name}>
                <span style={{fontSize:'22px',opacity:got?1:.4,filter:got?'none':'grayscale(1)'}}>{fam.icon}</span>
              </TierRing>
              <div style={{fontSize:'10px',fontWeight:700,marginTop:'6px',color:got?v('--ink'):v('--muted'),lineHeight:1.2}}>{fam.label}</div>
              <div style={{fontSize:'9px',fontWeight:800,marginTop:'1px',color:got?v('--orange'):v('--subtle'),textTransform:'uppercase',letterSpacing:'.03em'}}>
                {got ? prog.current.name : '—'}
              </div>
              {!prog.maxed && (
                <div style={{marginTop:'6px',height:'4px',borderRadius:'99px',background:v('--border'),overflow:'hidden',width:'100%'}}>
                  <div style={{height:'100%',width:`${Math.round(prog.pct*100)}%`,background:v('--orange'),opacity:.6}}/>
                </div>
              )}
            </button>
          );
        })}
        {SIMPLE.map(b => {
          const prog = simpleProgress(b, bds, totals);
          const got = prog.pct >= 1;
          return (
            <button key={b.id} onClick={()=>setSelected({kind:'simple',data:b})} title={b.desc} style={tileStyle(got)}>
              <div style={{fontSize:'26px',marginBottom:'5px',opacity:got?1:.4,filter:got?'none':'grayscale(1)'}}>{b.icon}</div>
              <div style={{fontSize:'10px',fontWeight:700,color:got?v('--ink'):v('--muted'),lineHeight:1.25}}>{b.label}</div>
              {!got && (
                <div style={{marginTop:'6px',height:'4px',borderRadius:'99px',background:v('--border'),overflow:'hidden',width:'100%'}}>
                  <div style={{height:'100%',width:`${Math.round(prog.pct*100)}%`,background:v('--orange'),opacity:.6}}/>
                </div>
              )}
            </button>
          );
        })}
      </div>
      {selected && <BadgeDetailModal selected={selected} bds={bds} totals={totals} onClose={()=>setSelected(null)}/>}
    </>
  );
};

const BadgeDetailModal = ({ selected, bds, totals, onClose }) => {
  const isTiered = selected.kind === 'tiered';
  const fam = isTiered ? selected.data : null;
  const badge = !isTiered ? selected.data : null;
  const tProg = isTiered ? tierProgress(fam, bds, totals) : null;
  const sProg = !isTiered ? simpleProgress(badge, bds, totals) : null;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:'1.25rem',backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:v('--surface'),borderRadius:'18px',border:`1px solid ${v('--border')}`,maxWidth:'380px',width:'100%',padding:'1.5rem',textAlign:'center',boxShadow:'0 16px 48px rgba(0,0,0,.25)'}}>

        {isTiered ? (
          <>
            <TierRing tierName={tProg.current?.name} size={84}>
              <span style={{fontSize:'40px',opacity:tProg.tierIndex>=0?1:.4,filter:tProg.tierIndex>=0?'none':'grayscale(1)'}}>{fam.icon}</span>
            </TierRing>
            <div style={{fontFamily:D,fontSize:'1.3rem',letterSpacing:'.03em',color:v('--ink'),marginTop:'12px'}}>{fam.label}</div>
            <div style={{fontSize:'10px',fontWeight:800,color:tProg.tierIndex>=0?v('--orange'):v('--subtle'),textTransform:'uppercase',letterSpacing:'.06em',marginTop:'2px'}}>
              {tProg.tierIndex>=0 ? `Palier actuel : ${tProg.current.name}` : 'Aucun palier atteint'}
            </div>

            <div style={{marginTop:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
              {fam.tiers.map((t,i) => {
                const done = i <= tProg.tierIndex;
                const isNext = i === tProg.tierIndex+1;
                return (
                  <div key={t.name} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',borderRadius:'9px',background:isNext?v('--surface2'):'transparent',border:isNext?`1px dashed ${v('--border')}`:'1px solid transparent'}}>
                    <div style={{width:'26px',height:'26px',borderRadius:'50%',background:done?tierColor(t.name):v('--border'),display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',color:'#fff',flexShrink:0,fontWeight:800}}>
                      {done ? '✓' : ''}
                    </div>
                    <div style={{flex:1,textAlign:'left'}}>
                      <div style={{fontSize:'12.5px',fontWeight:700,color:done?v('--ink'):v('--muted')}}>{t.name}</div>
                      <div style={{fontSize:'11px',color:v('--subtle')}}>{t.target} {fam.unit}</div>
                    </div>
                    {isNext && <div style={{fontSize:'11px',fontWeight:700,color:v('--orange')}}>{tProg.value}/{t.target}</div>}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div style={{fontSize:'52px',marginBottom:'10px',opacity:sProg.pct>=1?1:.4,filter:sProg.pct>=1?'none':'grayscale(1)'}}>{badge.icon}</div>
            <div style={{fontFamily:D,fontSize:'1.3rem',letterSpacing:'.03em',color:sProg.pct>=1?v('--orange'):v('--ink')}}>{badge.label}</div>
            <div style={{fontSize:'10px',fontWeight:700,color:sProg.pct>=1?'#16A34A':v('--subtle'),textTransform:'uppercase',letterSpacing:'.06em',marginTop:'4px'}}>
              {sProg.pct>=1 ? '✓ Débloqué' : 'À débloquer'}
            </div>
            <div style={{fontSize:'13.5px',color:v('--muted'),lineHeight:1.6,marginTop:'14px'}}>{badge.desc}</div>
            {sProg.pct<1 && (
              <div style={{marginTop:'18px'}}>
                <div style={{height:'10px',borderRadius:'99px',background:v('--border'),overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${Math.round(sProg.pct*100)}%`,background:v('--orange'),borderRadius:'99px',transition:'width .4s'}}/>
                </div>
                <div style={{fontSize:'12px',fontWeight:700,color:v('--muted'),marginTop:'6px'}}>{sProg.current} / {sProg.target} {badge.unit}</div>
              </div>
            )}
          </>
        )}

        <button onClick={onClose} style={{marginTop:'20px',width:'100%',padding:'11px',border:'none',borderRadius:'11px',background:v('--orange'),color:'#fff',fontFamily:F,fontSize:'14px',fontWeight:700,cursor:'pointer'}}>
          Fermer
        </button>
      </div>
    </div>
  );
};

/* ══ Page Stats principale ══════════════════════════════════ */
export default function StatsPage({ bds, isDark }) {
  const stats = useMemo(() => {
    const total = bds.length;
    const lus   = bds.filter(b=>b.lu).length;
    const avecPhotos = bds.filter(b=>b.photos?.length>0).length;

    // Valeurs
    const valAchat = bds.reduce((s,b)=>s+(parseFloat(b.prixAchat)||0),0);
    const valVente = bds.filter(b=>b.statut==='vendre').reduce((s,b)=>s+(parseFloat(b.prixVente)||0),0);
    const nbPrix   = bds.filter(b=>b.prixAchat).length;
    const prixMoyen = nbPrix>0 ? valAchat/nbPrix : 0;

    // Statuts
    const parStatut = STATUTS.map(s=>({
      label:s.label, val:bds.filter(b=>b.statut===s.value).length, color:s.color,
    })).filter(x=>x.val>0);

    // Genres (top 10)
    const genreMap = {};
    bds.forEach(b=>{ if(b.genre){ genreMap[b.genre]=(genreMap[b.genre]||0)+1; } });
    const parGenre = Object.entries(genreMap).sort((a,b)=>b[1]-a[1]).slice(0,10)
      .map(([l,v])=>({label:l,val:v,color:genreColor(l)}));

    // Éditeurs (top 8)
    const editMap = {};
    bds.forEach(b=>{ if(b.editeur){ editMap[b.editeur]=(editMap[b.editeur]||0)+1; } });
    const parEditeur = Object.entries(editMap).sort((a,b)=>b[1]-a[1]).slice(0,8)
      .map(([l,v],i)=>({label:l,val:v,color:PALETTE[i%PALETTE.length]}));

    // Séries + complétude
    const serieMap = {};
    bds.forEach(b=>{ if(b.serie){ if(!serieMap[b.serie]) serieMap[b.serie]=[]; serieMap[b.serie].push(b); } });
    const topSeries = Object.entries(serieMap).sort((a,b)=>b[1].length-a[1].length).slice(0,8)
      .map(([name,list],i)=>({ label:name, val:countUniqueNums(list), color:PALETTE[i%PALETTE.length] }));

    // Années d'achat
    const yearMap = {};
    bds.forEach(b=>{ if(b.dateAchat){ const y=b.dateAchat.slice(0,4); yearMap[y]=(yearMap[y]||0)+1; } });
    const parAnneeAchat = Object.entries(yearMap).sort((a,b)=>a[0].localeCompare(b[0]))
      .map(([y,n])=>({label:y,val:n}));

    // Notes distribution
    const notesDist = [1,2,3,4,5].map(n=>({ label:'★'.repeat(n), val:bds.filter(b=>b.note===n).length, color:PALETTE[n-1] }));

    // Lieux d'achat
    const lieuMap = {};
    bds.forEach(b=>{ if(b.lieuAchat){ lieuMap[b.lieuAchat]=(lieuMap[b.lieuAchat]||0)+1; } });
    const parLieu = Object.entries(lieuMap).sort((a,b)=>b[1]-a[1]).slice(0,8)
      .map(([l,v],i)=>({label:l,val:v,color:PALETTE[i%PALETTE.length]}));

    // EO, dédicaces
    const eo = bds.filter(b=>b.eo).length;
    const dedicaces = bds.filter(b=>b.dedicace).length;

    return { total,lus,avecPhotos,valAchat,valVente,prixMoyen,nbPrix,parStatut,parGenre,parEditeur,topSeries,parAnneeAchat,notesDist,parLieu,eo,dedicaces };
  }, [bds]);

  if (!bds?.length || !stats) return (
    <div style={{textAlign:'center',padding:'4rem 2rem',color:v('--muted'),fontFamily:F}}>
      <div style={{fontSize:'3rem',marginBottom:'1rem'}}>📊</div>
      <div style={{fontFamily:D,fontSize:'1.6rem',color:v('--orange'),letterSpacing:'.04em',marginBottom:'8px'}}>Pas encore de données</div>
      <div style={{fontSize:'14px'}}>Ajoute des BD pour voir tes statistiques.</div>
    </div>
  );

  const maxGenre   = stats.parGenre[0]?.val  || 1;
  const maxEditeur = stats.parEditeur[0]?.val || 1;
  const maxSerie   = stats.topSeries[0]?.val  || 1;
  const maxLieu    = stats.parLieu[0]?.val    || 1;
  const maxAnnee   = Math.max(...stats.parAnneeAchat.map(x=>x.val), 1);

  return (
    <div>
      <div style={{fontFamily:D,fontSize:'1.4rem',letterSpacing:'.05em',color:v('--orange'),marginBottom:'1.25rem'}}>
        Statistiques de ta collection
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(280px,100%),1fr))',gap:'12px'}}>

        {/* ── Chiffres clés ── */}
        <Card title="Chiffres clés">
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,minmax(0,1fr))',gap:'16px'}}>
            <BigNum val={stats.total}   label="Albums"/>
            <BigNum val={`${Math.round(stats.lus/stats.total*100)}%`} label="Lus" color={v('--green')}/>
            <BigNum val={stats.avecPhotos} label="Avec photos" color={v('--blue')}/>
            {stats.valAchat>0&&<BigNum val={`${Math.round(stats.valAchat)}€`} label="Investi" color={v('--muted')} sub={stats.nbPrix>0?`${stats.prixMoyen.toFixed(2)}€ moy.`:null}/>}
            {stats.valVente>0&&<BigNum val={`${Math.round(stats.valVente)}€`} label="Revente" color={v('--green')}/>}
            {stats.eo>0&&<BigNum val={stats.eo} label="1ères éd." color={v('--yellow')}/>}
            {stats.dedicaces>0&&<BigNum val={stats.dedicaces} label="Dédicacés" color={v('--purple')}/>}
          </div>
        </Card>

        {/* ── Badges ── */}
        <Card title="🏆 Badges" span={2}>
          <BadgeGrid bds={bds}/>
        </Card>

        {/* ── Statuts (donut) ── */}
        {stats.parStatut.length>0&&(
          <Card title="Répartition par statut">
            <div style={{display:'flex',alignItems:'center',gap:'20px',flexWrap:'wrap'}}>
              <DonutChart data={stats.parStatut.map(s=>({val:s.val,color:s.color}))} size={120}/>
              <div style={{flex:1,minWidth:'120px'}}>
                {stats.parStatut.map(s=>(
                  <div key={s.label} style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'6px'}}>
                    <div style={{width:'10px',height:'10px',borderRadius:'50%',background:s.color,flexShrink:0}}/>
                    <span style={{fontSize:'12px',fontWeight:500,color:v('--ink'),fontFamily:F,flex:1}}>{s.label}</span>
                    <span style={{fontSize:'12px',fontWeight:700,color:s.color,fontFamily:F}}>{s.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* ── Notes (donut) ── */}
        {stats.notesDist.some(n=>n.val>0)&&(
          <Card title="Mes notes">
            <div style={{display:'flex',alignItems:'center',gap:'20px',flexWrap:'wrap'}}>
              <DonutChart data={stats.notesDist.filter(n=>n.val>0)} size={100}/>
              <div style={{flex:1}}>
                {[...stats.notesDist].reverse().filter(n=>n.val>0).map(n=>(
                  <div key={n.label} style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'5px'}}>
                    <span style={{fontSize:'12px',color:v('--yellow'),fontFamily:F,width:'50px',flexShrink:0}}>{n.label}</span>
                    <div style={{flex:1,height:'5px',background:v('--border'),borderRadius:'99px',overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${n.val/Math.max(...stats.notesDist.map(x=>x.val))*100}%`,background:v('--yellow'),borderRadius:'99px'}}/>
                    </div>
                    <span style={{fontSize:'11px',fontWeight:700,color:v('--muted'),fontFamily:F,width:'20px',textAlign:'right'}}>{n.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* ── Top genres ── */}
        {stats.parGenre.length>0&&(
          <Card title="Top genres">
            {stats.parGenre.map(g=><HBar key={g.label} label={g.label} val={g.val} max={maxGenre} color={g.color} total={stats.total}/>)}
          </Card>
        )}

        {/* ── Top éditeurs ── */}
        {stats.parEditeur.length>0&&(
          <Card title="Top éditeurs">
            {stats.parEditeur.map(e=><HBar key={e.label} label={e.label} val={e.val} max={maxEditeur} color={e.color} total={stats.total}/>)}
          </Card>
        )}

        {/* ── Top séries ── */}
        {stats.topSeries.length>0&&(
          <Card title="Séries les plus représentées">
            {stats.topSeries.map(s=><HBar key={s.label} label={s.label} val={s.val} max={maxSerie} color={s.color} total={stats.total}/>)}
          </Card>
        )}

        {/* ── Lieux d'achat ── */}
        {stats.parLieu.length>0&&(
          <Card title="Où j'achète mes BD">
            {stats.parLieu.map(l=><HBar key={l.label} label={l.label} val={l.val} max={maxLieu} color={l.color} total={stats.total}/>)}
          </Card>
        )}

        {/* ── Acquisitions par année ── */}
        {stats.parAnneeAchat.length>1&&(
          <Card title="Acquisitions par année" span={2}>
            <div style={{display:'flex',alignItems:'flex-end',gap:'6px',height:'100px',overflow:'hidden'}}>
              {stats.parAnneeAchat.map(({label,val})=>(
                <div key={label} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'4px',flex:1,minWidth:0}}>
                  <div style={{fontSize:'10px',fontWeight:700,color:v('--orange'),fontFamily:F}}>{val}</div>
                  <div style={{width:'100%',background:v('--orange'),borderRadius:'4px 4px 0 0',height:`${Math.round(val/maxAnnee*68)}px`,minHeight:'4px',transition:'height .5s ease'}}/>
                  <div style={{fontSize:'10px',color:v('--muted'),fontFamily:F,whiteSpace:'nowrap'}}>{label}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
